import { nyxHttp } from "@/services/api-service";
import Logger from "@/helpers/logger";
import type { TPendingAccount } from "@/store/modules/mail/mailbox-nudge.store";

/**
 * Email-suggestion (pending mailbox accounts) API — Control Panel endpoints
 * Calls go through `nyxHttp` (Flockmail base URL + x-a1 auth);
 * it shares $http's interceptors, so `bundleId` is still auto-appended as a
 * query param (getCommonParams) and `.data` is unwrapped. `bundleId` is also
 * passed in the add/delete body per the API spec.
 *
 * `get` returns rows in the backend's own shape (`TPendingAccount`), wrapped
 * in a `{ suggestions: [...] }` envelope; no per-row transformation happens
 * anywhere — the service unwraps the array and the store consumes the rows
 * verbatim.
 */

export type TGetSuggestionsResponse = {
  suggestions: TPendingAccount[];
};

export type TAddSuggestionPayload = {
  suggestedEmails: string[];
  requesterName: string;
  requesterEmail: string;
  bundleId: number | string;
};

export type TAddSuggestionResponse = {
  suggestionsAdded: string[];
  alreadyExists: string[];
};

export type TRemoveSuggestionPayload = {
  suggestedEmail: string;
  bundleId: number | string;
};

class PendingAccountsApi {
  async get(): Promise<TGetSuggestionsResponse> {
    try {
      return await nyxHttp.get("r/email/suggestions/cp/get");
    } catch (e) {
      Logger.error("`pendingAccounts.get` api", e);
      throw e;
    }
  }

  async add(payload: TAddSuggestionPayload): Promise<TAddSuggestionResponse> {
    try {
      return await nyxHttp.post("r/email/suggestions/cp/add", payload);
    } catch (e) {
      Logger.error(
        "`pendingAccounts.add` api",
        { suggestedEmails: payload.suggestedEmails, bundleId: payload.bundleId },
        e
      );
      throw e;
    }
  }

  async remove(payload: TRemoveSuggestionPayload): Promise<void> {
    try {
      return await nyxHttp.post("r/email/suggestion/cp/delete", payload);
    } catch (e) {
      Logger.error(
        "`pendingAccounts.remove` api",
        { suggestedEmail: payload.suggestedEmail, bundleId: payload.bundleId },
        e
      );
      throw e;
    }
  }
}

export default new PendingAccountsApi();
